import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { format } from 'date-fns';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiEdit2,
  FiFileText,
  FiFlag,
  FiHelpCircle,
  FiPlus,
  FiSend,
  FiTrash2,
} from 'react-icons/fi';

import BasePage from '../../../components/BasePage';
import LoadingState from '../../../components/LoadingState';

import api from '../../../services/api';
import { useToast } from '../../../hooks/useToast';

import EditResearchModal from '../EditResearchModal';
import DeleteResearchModal from '../DeleteResearchModal';
import AddQuestionModal from './AddQuestionModal';
import RemoveQuestionModal from './RemoveQuestionModal';
import EditQuestionModal from './EditQuestionModal';

import {
  AnswerItem,
  AnswerList,
  BackLink,
  Button,
  Card,
  CardHeader,
  EditGrid,
  IconAction,
  InfoList,
  InfoRow,
  PageContainer,
  PageHeader,
  QuestionCard,
  QuestionEmptyState,
  QuestionList,
  SidebarFooter,
  StatusBadge,
} from './styles';

interface IAnswer {
  id: string;
  text: string;
}

interface IQuestion {
  id: string;
  text: string;
  type: string;
  answers: IAnswer[];
}

interface IResearch {
  id: string;
  title: string;
  status: string;
  description: string;
  start_date: Date;
  finish_date: Date;
  questions: IQuestion[];
}

interface ISelectedQuestion {
  questionId: string;
  action: 'edit' | 'remove';
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicada',
  finished: 'Finalizada',
};

const EditResearch: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [research, setResearch] = useState<IResearch>({} as IResearch);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showRemoveQuestionModal, setShowRemoveQuestionModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<ISelectedQuestion>(
    {} as ISelectedQuestion,
  );
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);

  const publishResearch = useCallback(async () => {
    try {
      const updatedResearch = await api.patch(`/research/${id}`, {
        status: 'published',
      });

      addToast({
        title: 'Sucesso',
        body: 'A pesquisa foi publicada com sucesso.',
      });

      setResearch(updatedResearch.data);
    } catch (error) {
      addToast({
        title: 'Erro',
        body: 'Ocorreu algum erro ao tentar publicar a pesquisa.',
      });
    }
  }, [addToast, id]);

  const finishResearch = useCallback(async () => {
    try {
      const updatedResearch = await api.patch(`/research/${id}`, {
        status: 'finished',
      });

      addToast({
        title: 'Sucesso',
        body: 'A pesquisa foi finalizada com sucesso.',
      });

      setResearch(updatedResearch.data);
    } catch (error) {
      addToast({
        title: 'Erro',
        body: 'Ocorreu algum erro ao tentar finalizar a pesquisa.',
      });
    }
  }, [addToast, id]);

  useEffect(() => {
    if (selectedQuestion.action === 'edit') {
      setShowEditQuestionModal(true);
    }
  }, [selectedQuestion]);

  const editQuestionModal = useCallback((questionId: string) => {
    setSelectedQuestion({ action: 'edit', questionId });
  }, []);

  const removeQuestionModal = useCallback((questionId: string) => {
    setSelectedQuestion({ action: 'remove', questionId });
    setShowRemoveQuestionModal(true);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      const loadedResearch = await api.get(`/research/${id}`);
      setResearch(loadedResearch.data);
      setLoading(false);
    };

    if (loading) {
      loadData();
    }
  }, [loading, id]);

  const statusTone = useMemo<'success' | 'warning' | 'neutral'>(() => {
    if (research.status === 'published') return 'success';
    if (research.status === 'draft') return 'warning';
    return 'neutral';
  }, [research.status]);

  const isDraft = research.status === 'draft';
  const isPublished = research.status === 'published';

  const totalQuestions = research.questions?.length || 0;

  const formattedStart = research.start_date
    ? format(new Date(research.start_date), "dd/MM/yyyy 'às' HH:mm")
    : null;
  const formattedFinish = research.finish_date
    ? format(new Date(research.finish_date), "dd/MM/yyyy 'às' HH:mm")
    : null;

  return (
    <BasePage title={!research ? 'Pesquisa' : `Pesquisa - ${research.title}`}>
      {loading && <LoadingState />}
      {!loading && research && (
        <Container fluid>
          <PageContainer>
            <div>
              <BackLink type="button" onClick={() => history.push('/research')}>
                <FiArrowLeft size={14} />
                Voltar para pesquisas
              </BackLink>

              <PageHeader>
                <div className="titles">
                  <div className="title-row">
                    <h1>{research.title}</h1>
                    <StatusBadge $tone={statusTone}>
                      <span className="dot" />
                      {STATUS_LABEL[research.status] || research.status}
                    </StatusBadge>
                  </div>
                  {research.description && (
                    <span className="subtitle">{research.description}</span>
                  )}
                </div>

                <div className="actions">
                  {isDraft && (
                    <Button
                      $variant="secondary"
                      type="button"
                      onClick={() => setShowEditModal(true)}
                    >
                      <FiEdit2 size={13} />
                      Editar detalhes
                    </Button>
                  )}
                  {isDraft && (
                    <Button
                      type="button"
                      onClick={publishResearch}
                      disabled={totalQuestions === 0}
                    >
                      <FiSend size={13} />
                      Publicar pesquisa
                    </Button>
                  )}
                  {isPublished && (
                    <Button type="button" onClick={finishResearch}>
                      <FiCheckCircle size={13} />
                      Finalizar
                    </Button>
                  )}
                </div>
              </PageHeader>
            </div>

            <EditGrid>
              {/* ── Questões ── */}
              <Card>
                <CardHeader>
                  <div className="titles">
                    <h3>Questões do formulário</h3>
                    <span className="subtitle">
                      {totalQuestions === 0
                        ? 'Nenhuma questão cadastrada'
                        : `${totalQuestions} ${
                            totalQuestions === 1 ? 'questão' : 'questões'
                          } cadastradas`}
                    </span>
                  </div>
                  {isDraft && (
                    <Button
                      type="button"
                      $size="sm"
                      onClick={() => setShowAddQuestionModal(true)}
                    >
                      <FiPlus size={13} />
                      Adicionar questão
                    </Button>
                  )}
                </CardHeader>

                {totalQuestions === 0 ? (
                  <QuestionEmptyState>
                    <div className="icon">
                      <FiHelpCircle size={20} />
                    </div>
                    <strong>Sua pesquisa ainda não tem questões</strong>
                    {isDraft
                      ? 'Clique em "Adicionar questão" para começar.'
                      : 'Esta pesquisa foi criada sem questões.'}
                  </QuestionEmptyState>
                ) : (
                  <QuestionList>
                    {research.questions.map((question, index) => (
                      <QuestionCard key={question.id}>
                        <div className="question-header">
                          <span className="index-badge">{index + 1}</span>
                          <div className="question-body">
                            <span className="question-text">
                              {question.text}
                            </span>
                            <span className="question-meta">
                              {question.type === 'radio' ? (
                                <>
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      background: '#006633',
                                      display: 'inline-block',
                                    }}
                                  />
                                  Única seleção
                                </>
                              ) : (
                                <>
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: 2,
                                      background: '#f38725',
                                      display: 'inline-block',
                                    }}
                                  />
                                  Múltipla seleção
                                </>
                              )}
                              {' · '}
                              {question.answers.length}
                              {' '}
                              {question.answers.length === 1
                                ? 'opção'
                                : 'opções'}
                            </span>
                          </div>
                          {isDraft && (
                            <div className="question-actions">
                              <IconAction
                                type="button"
                                aria-label="Editar questão"
                                onClick={() => editQuestionModal(question.id)}
                              >
                                <FiEdit2 size={13} />
                              </IconAction>
                              <IconAction
                                type="button"
                                aria-label="Remover questão"
                                $danger
                                onClick={() => removeQuestionModal(question.id)}
                              >
                                <FiTrash2 size={13} />
                              </IconAction>
                            </div>
                          )}
                        </div>

                        <AnswerList>
                          {question.answers.map(answer => (
                            <AnswerItem
                              key={answer.id}
                              $variant={
                                question.type === 'radio' ? 'radio' : 'checkbox'
                              }
                            >
                              <span className="marker" />
                              {answer.text}
                            </AnswerItem>
                          ))}
                        </AnswerList>
                      </QuestionCard>
                    ))}
                  </QuestionList>
                )}
              </Card>

              {/* ── Sidebar info ── */}
              <Card>
                <CardHeader>
                  <div className="titles">
                    <h3>Informações</h3>
                    <span className="subtitle">Detalhes da pesquisa</span>
                  </div>
                </CardHeader>

                <InfoList>
                  <InfoRow>
                    <span className="icon">
                      <FiFlag size={14} />
                    </span>
                    <div className="body">
                      <dt>Status</dt>
                      <dd>
                        {STATUS_LABEL[research.status] || research.status}
                      </dd>
                    </div>
                  </InfoRow>

                  <InfoRow>
                    <span className="icon">
                      <FiCalendar size={14} />
                    </span>
                    <div className="body">
                      <dt>Início</dt>
                      <dd>{formattedStart || '—'}</dd>
                    </div>
                  </InfoRow>

                  <InfoRow>
                    <span className="icon">
                      <FiClock size={14} />
                    </span>
                    <div className="body">
                      <dt>Encerramento</dt>
                      <dd>{formattedFinish || '—'}</dd>
                    </div>
                  </InfoRow>

                  <InfoRow>
                    <span className="icon">
                      <FiFileText size={14} />
                    </span>
                    <div className="body">
                      <dt>Descrição</dt>
                      <dd className={research.description ? '' : 'muted'}>
                        {research.description || 'Sem descrição'}
                      </dd>
                    </div>
                  </InfoRow>
                </InfoList>

                {isDraft && totalQuestions === 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      padding: '10px 12px',
                      background: '#fff4e0',
                      border: '1px solid #fcd9b0',
                      borderRadius: 10,
                      fontSize: 12,
                      color: '#b45309',
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    <FiAlertCircle
                      size={14}
                      style={{ flexShrink: 0, marginTop: 1 }}
                    />
                    Adicione ao menos uma questão antes de publicar a pesquisa.
                  </div>
                )}

                <SidebarFooter>
                  <Button
                    $variant="danger"
                    type="button"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <FiTrash2 size={13} />
                    Remover pesquisa
                  </Button>
                </SidebarFooter>
              </Card>
            </EditGrid>
          </PageContainer>
        </Container>
      )}

      <EditResearchModal
        handleShow={setShowEditModal}
        show={showEditModal}
        researchId={id}
        handleResearch={setResearch}
      />

      <DeleteResearchModal
        selectedResearch={id}
        handleShow={setShowDeleteModal}
        show={showDeleteModal}
        handleLoading={setLoading}
        handleResearches={() => null}
      />

      <AddQuestionModal
        show={showAddQuestionModal}
        handleShow={setShowAddQuestionModal}
        handleLoading={setLoading}
        handleResearch={setResearch}
        researchId={id}
      />

      <EditQuestionModal
        show={showEditQuestionModal}
        handleShow={setShowEditQuestionModal}
        questionId={selectedQuestion.questionId}
        handleSelected={setSelectedQuestion}
        handleLoading={setLoading}
        handleResearch={setResearch}
      />

      <RemoveQuestionModal
        show={showRemoveQuestionModal}
        handleShow={setShowRemoveQuestionModal}
        handleResearch={setResearch}
        handleLoading={setLoading}
        questionId={selectedQuestion.questionId}
        handleSelected={setSelectedQuestion}
      />
    </BasePage>
  );
};

export default EditResearch;
